/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils/DateTimeUtils';

import { Grid, Typography2 } from '@coursera/cds-core';

import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { getIsValidDeadlineCardDate } from 'bundles/degree-description/utils/utils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/degree-description';

const styles = {
  card: css`
    border-radius: 8px;
    box-shadow: rgb(31 31 31 / 10%) 0 3px 10px;
    padding: 20px;
    border: 1px solid var(--cds-color-neutral-disabled-strong);
  `,
  padding: css`
    padding: 15px 0;
  `,
};

const deadlineVariants = {
  enroll: 'Enroll',
  apply: 'Apply',
};

// Need to manually define these props because DateTime from Contentful is counted as an `any` type
type Props = {
  title?: string | null;
  deadlineType?: string | null;
  enrollApplyCustomText?: string | null;
  coursesStartCustomText?: string | null;
  enrollApplyStart?: string | null;
  enrollApplyEnd?: string | null;
  coursesStart?: string | null;
};

const DeadlineCard = ({
  title,
  enrollApplyStart: enrollStartDate,
  enrollApplyEnd: enrollEndDate,
  coursesStart: courseStartDate,
  deadlineType,
  enrollApplyCustomText,
  coursesStartCustomText,
}: Props) => {
  const { useGetViewDeadlineCardRef, trackClickDeadlineCard } = useDegreeDescriptionEventingContext();
  const trackDeadlineCardClick = () => trackClickDeadlineCard(title, enrollStartDate, enrollEndDate, courseStartDate);
  const visibilityTrackingRef = useGetViewDeadlineCardRef(title, enrollStartDate, enrollEndDate, courseStartDate);

  if (!enrollStartDate && !enrollEndDate && !courseStartDate) return null;

  const formattedEnrollStartDate =
    enrollStartDate && formatDateTimeDisplay(enrollStartDate, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY);
  const formattedEnrollEndDate =
    enrollEndDate && formatDateTimeDisplay(enrollEndDate, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY);
  const formattedCoursesStartDate =
    courseStartDate && formatDateTimeDisplay(courseStartDate, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY);

  let enrollText: string = _t('Enrollment is open soon');
  let coursesText: string | undefined;

  const setCustomEnrollText = () => {
    if (formattedEnrollStartDate && !formattedEnrollEndDate) {
      enrollText = `${enrollApplyCustomText} ${formattedEnrollStartDate}`;
    } else if (!formattedEnrollStartDate && formattedEnrollEndDate) {
      enrollText = `${enrollApplyCustomText} ${formattedEnrollEndDate}`;
    } else if (formattedEnrollStartDate && formattedEnrollEndDate) {
      enrollText = `${enrollApplyCustomText} ${formattedEnrollStartDate} - ${formattedEnrollEndDate}`;
    }
  };

  const setDefaultEnrollText = () => {
    if (deadlineType === deadlineVariants.enroll) {
      if (formattedEnrollStartDate && formattedEnrollEndDate) {
        enrollText = _t('Enroll from #{enrollmentStartDate} to #{enrollmentEndDate}', {
          enrollmentStartDate: formattedEnrollStartDate,
          enrollmentEndDate: formattedEnrollEndDate,
        });
      } else if (formattedEnrollStartDate && !formattedEnrollEndDate) {
        enrollText = _t('Enroll starting #{enrollmentStartDate}', {
          enrollmentStartDate: formattedEnrollStartDate,
        });
      } else if (!formattedEnrollStartDate && formattedEnrollEndDate) {
        enrollText = _t('Enroll by #{enrollmentEndDate}', {
          enrollmentEndDate: formattedEnrollEndDate,
        });
      }
    } else if (deadlineType === deadlineVariants.apply) {
      enrollText = _t('Submit your applications now');
      if (formattedEnrollStartDate && formattedEnrollEndDate) {
        enrollText = _t('Submit your application from #{applicationsStartDate} to #{applicationsEndDate}', {
          applicationsStartDate: formattedEnrollStartDate,
          applicationsEndDate: formattedEnrollEndDate,
        });
      } else if (formattedEnrollStartDate && !formattedEnrollEndDate) {
        enrollText = _t('Submit your application starting #{applicationsStartDate}', {
          applicationsStartDate: formattedEnrollStartDate,
        });
      } else if (!formattedEnrollStartDate && formattedEnrollEndDate) {
        enrollText = _t('Submit your application by #{applicationsEndDate}', {
          applicationsEndDate: formattedEnrollEndDate,
        });
      }
    }
  };

  const setCustomClassesText = () => {
    if (formattedCoursesStartDate) {
      coursesText = `${coursesStartCustomText} ${formattedCoursesStartDate}`;
    }
  };

  const setDefaultClassesText = () => {
    coursesText = _t('Courses start on #{courseStartDate}', {
      courseStartDate: formattedCoursesStartDate,
    });
  };

  if (enrollApplyCustomText) {
    setCustomEnrollText();
  } else {
    setDefaultEnrollText();
  }

  if (coursesStartCustomText) {
    setCustomClassesText();
  } else {
    setDefaultClassesText();
  }

  return (
    <Grid item css={styles.card}>
      <div ref={visibilityTrackingRef}>
        <TrackedDiv
          trackingName="deadline_card"
          withVisibilityTracking
          data={{
            title: title ?? 'deadline card',
            ...(getIsValidDeadlineCardDate(enrollStartDate) && {
              enrollStartDate: Number(formatDateTimeDisplay(enrollStartDate!, MOMENT_FORMATS.TIMESTAMP)),
            }),
            ...(getIsValidDeadlineCardDate(enrollEndDate) && {
              enrollEndDate: Number(formatDateTimeDisplay(enrollEndDate!, MOMENT_FORMATS.TIMESTAMP)),
            }),
            ...(getIsValidDeadlineCardDate(courseStartDate) && {
              courseStartDate: Number(formatDateTimeDisplay(courseStartDate!, MOMENT_FORMATS.TIMESTAMP)),
            }),
          }}
          onClick={trackDeadlineCardClick}
        >
          {title && (
            <Typography2 component="h3" variant="subtitleMedium">
              {title}
            </Typography2>
          )}
          <Typography2 component="p" variant="bodySecondary" css={styles.padding} color="supportText">
            {enrollText}
          </Typography2>
          {formattedCoursesStartDate && (
            <Typography2 component="p" variant="bodySecondary" color="supportText">
              {coursesText}
            </Typography2>
          )}
        </TrackedDiv>
      </div>
    </Grid>
  );
};

export default DeadlineCard;
